import { useTheme } from "@emotion/react";
import { faTrash } from "@fortawesome/pro-light-svg-icons";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { useForm, useStore } from "@tanstack/react-form";
import { Operator, ProviderType } from "@ternary/api-lib/analytics/enums";
import { Dimension } from "@ternary/api-lib/analytics/ui/types";
import { DimensionPreferenceEntity } from "@ternary/api-lib/core/types";
import {
  BillingRuleEntity,
  BillingRuleType,
  ReallocationConfigDynamic,
  ReallocationConfigStatic,
  ReallocationType,
} from "@ternary/api-lib/core/types/BillingRule";
import { TenantEntity, TenantType } from "@ternary/api-lib/core/types/Tenant";
import Box from "@ternary/api-lib/ui-lib/components/Box";
import Button from "@ternary/api-lib/ui-lib/components/Button";
import Flex from "@ternary/api-lib/ui-lib/components/Flex";
import Icon from "@ternary/api-lib/ui-lib/components/Icon";
import Text from "@ternary/api-lib/ui-lib/components/Text";
import Divider from "@ternary/web-ui-lib/components/Divider";
import { formatDate } from "date-fns";
import { isEqual, keyBy, orderBy, sortBy } from "lodash";
import React, { useMemo } from "react";
import { z } from "zod";
import { DimensionValuesMap } from "../../../../api/analytics/useGetDimensionValues";
import { CustomSelectInput } from "../../../../ui-lib/components/DateRangeControls/CustomSelectInput";
import { DatePicker } from "../../../../ui-lib/components/DateRangeControls/DatePicker";
import Dropdown from "../../../../ui-lib/components/Dropdown";
import Form, { FormField } from "../../../../ui-lib/components/Form";
import LoadingSpinner from "../../../../ui-lib/components/LoadingSpinner";
import Select from "../../../../ui-lib/components/Select";
import { LabeledSwitch } from "../../../../ui-lib/components/Switch";
import TextInput from "../../../../ui-lib/components/TextInput";
import copyText from "../../copyText";
import { BillingRuleFormApplyMargin } from "./BillingRuleFormApplyMargin";
import { BillingRuleFormPriority } from "./BillingRuleFormPriority";
import { BillingRuleFormReallocateCosts } from "./BillingRuleFormReallocateCosts";
import { BillingRuleFormRerate } from "./BillingRuleFormRerate";
import { FilterSection } from "./FilterSection";
import { FormData, SubmitValues } from "./types";

const defaultFilter = {
  displayName: "",
  operator: Operator.EQUALS,
  providerType: ProviderType.FOCUS,
  schemaName: "",
  values: [],
};

const mspChildTenantRuleTypes = [BillingRuleType.REALLOCATE_COSTS];

const directTenantRuleTypes = [
  BillingRuleType.ADJUST_TO_LIST,
  BillingRuleType.BACKOUT_LINE_ITEM,
  BillingRuleType.REALLOCATE_COSTS,
  BillingRuleType.RERATE,
];

const mspTenantRuleTypes = [
  BillingRuleType.ADJUST_TO_LIST,
  BillingRuleType.BACKOUT_LINE_ITEM,
  BillingRuleType.APPLY_MARGIN,
  BillingRuleType.RERATE,
];

const defaultValues: FormData = {
  adjustToList: false,
  applyToAllTenants: false,
  applyToTenants: [],
  endDate: null,
  filters: [defaultFilter],
  filtersDynamic: [defaultFilter],
  measure: null,
  metrics: [],
  minimalRules: [],
  minimumShare: 0,
  name: "",
  reallocationType: null,
  rulePriority: [],
  ruleType: null,
  setRulePriority: false,
  startDate: null,
  targetsStatic: [],
  usageGroupingDimensions: [],
  value: 0,
  weightingKeysSearchText: "",
};

interface Props {
  availableDimensions: Dimension[];
  billingRule?: BillingRuleEntity;
  billingRules: BillingRuleEntity[];
  dimensionPreferences: DimensionPreferenceEntity[];
  dimensionValues: DimensionValuesMap;
  isLoadingBillingRules: boolean;
  isLoadingDimensionPreferences: boolean;
  isLoadingDimensionValues: boolean;
  isLoadingSubAccountIDs: boolean;
  isMspAdmin: boolean;
  isProcessing: boolean;
  subAccountIDs: string[];
  tenants: TenantEntity[];
  tenantType: TenantType;
  onInteraction: (interaction: BillingRuleForm.Interaction) => void;
}

export function BillingRuleForm(props: Props) {
  const theme = useTheme();

  //
  // Form
  //

  const _defaultValues = useMemo(() => {
    if (!props.billingRule) {
      const minimalRules = sortBy(
        [
          { id: "new", name: "", priority: 1 },
          ...props.billingRules.map((rule) => ({
            id: rule.id,
            name: rule.name,
            priority: rule.priority + 1,
          })),
        ],
        "priority"
      );

      return {
        ...defaultValues,
        minimalRules,
      };
    }

    const tenantsKeyedByID = keyBy(props.tenants, "id");

    const applyToTenants = props.billingRule.applyToTenantIDs.map((id) => ({
      id,
      name: tenantsKeyedByID[id]?.name ?? "",
    }));

    const minimalRules = sortBy(
      props.billingRules.map((rule) => ({
        id: rule.id,
        name: rule.name,
        priority: rule.priority,
      })),
      "priority"
    );

    const metaValues: Partial<FormData> = {};

    switch (props.billingRule.type) {
      case BillingRuleType.ADJUST_TO_LIST: {
        metaValues.filters = props.billingRule.meta.filters ?? [];
        break;
      }
      case BillingRuleType.APPLY_MARGIN: {
        metaValues.adjustToList = props.billingRule.meta.adjustToList;
        metaValues.filters = props.billingRule.meta.filters ?? [];
        metaValues.metrics = props.billingRule.meta.metrics ?? [];
        metaValues.value = props.billingRule.meta.value;
        break;
      }
      case BillingRuleType.BACKOUT_LINE_ITEM: {
        metaValues.filters = props.billingRule.meta.filters ?? [];
        break;
      }
      case BillingRuleType.REALLOCATE_COSTS: {
        const reallocationType =
          props.billingRule.meta.reallocationConfig?.type;

        if (reallocationType === ReallocationType.DYNAMIC) {
          const config = props.billingRule.meta
            .reallocationConfig as ReallocationConfigDynamic;

          metaValues.filters = props.billingRule.meta.filters ?? [];
          metaValues.filtersDynamic = config.filters ?? [];
          metaValues.measure = config.measure;
          metaValues.minimumShare = config.minimumShare;
          metaValues.reallocationType = reallocationType;
          metaValues.usageGroupingDimensions = config.usageGroupingDimensions;
        } else if (reallocationType === ReallocationType.STATIC) {
          const config = props.billingRule.meta
            .reallocationConfig as ReallocationConfigStatic;

          metaValues.filters = props.billingRule.meta.filters ?? [];
          metaValues.reallocationType = reallocationType;
          metaValues.targetsStatic = config.targets.map((target) => ({
            identifier: {
              labels: Object.entries(target.identifier.labels).map(
                ([key, value]) => ({
                  key,
                  value,
                })
              ),
              subAccountID: target.identifier.subAccountID,
            },
            share: target.share,
          }));
        }
        break;
      }
      case BillingRuleType.RERATE: {
        metaValues.adjustToList = props.billingRule.meta.adjustToList;
        metaValues.filters = props.billingRule.meta.filters ?? [];
        metaValues.metrics = props.billingRule.meta.metrics ?? [];
        metaValues.value = props.billingRule.meta.value;
        break;
      }
    }

    return {
      ...defaultValues,
      ...metaValues,
      applyToAllTenants: props.billingRule.applyToAllTenants,
      applyToTenants,
      endDate: props.billingRule.endTime
        ? new Date(props.billingRule.endTime)
        : null,
      minimalRules,
      name: props.billingRule.name,
      ruleType: props.billingRule.type,
      startDate: new Date(props.billingRule.startTime),
    };
  }, [props.billingRule]);

  const form = useForm<FormData>({
    defaultValues: _defaultValues,
    onSubmit: ({ value }) => {
      props.onInteraction({
        type: BillingRuleForm.INTERACTION_SUBMIT_BUTTON_CLICKED,
        values: value as SubmitValues,
      });
    },
  });

  const formState = useStore(form.store, (state) => ({
    ruleType: state.values.ruleType,
  }));

  //
  // Render
  //

  let ruleTypes: BillingRuleType[] = [];

  if (props.tenantType === TenantType.MSP_PARENT) {
    ruleTypes = mspTenantRuleTypes;
  } else if (props.tenantType === TenantType.DIRECT_CUSTOMER) {
    ruleTypes = directTenantRuleTypes;
  } else {
    ruleTypes = mspChildTenantRuleTypes;
  }

  const ruleTypeOptions = ruleTypes.map((type) => ({
    label: copyText[`ruleTypeOptionLabel_${type}`],
    value: type,
  }));

  function renderFormContent() {
    switch (formState.ruleType) {
      case BillingRuleType.ADJUST_TO_LIST: {
        return (
          <Box
            backgroundColor={theme.panel_backgroundColor}
            borderRadius={`0 0 ${theme.borderRadius_2} ${theme.borderRadius_2}`}
          >
            <Divider margin={0} />
            <FilterSection
              key={`filter-section-${formState.ruleType}`}
              availableDimensions={props.availableDimensions}
              dimensionPreferences={props.dimensionPreferences}
              dimensionValues={props.dimensionValues}
              form={form}
              isLoadingDimensionPreferences={
                props.isLoadingDimensionPreferences
              }
              isLoadingDimensionValues={props.isLoadingDimensionValues}
              onInteraction={props.onInteraction}
            />
          </Box>
        );
      }
      case BillingRuleType.APPLY_MARGIN: {
        return (
          <BillingRuleFormApplyMargin
            availableDimensions={props.availableDimensions}
            dimensionPreferences={props.dimensionPreferences}
            dimensionValues={props.dimensionValues}
            form={form}
            isLoadingDimensionPreferences={props.isLoadingDimensionPreferences}
            isLoadingDimensionValues={props.isLoadingDimensionValues}
            onInteraction={props.onInteraction}
          />
        );
      }
      case BillingRuleType.BACKOUT_LINE_ITEM: {
        return (
          <Box
            backgroundColor={theme.panel_backgroundColor}
            borderRadius={`0 0 ${theme.borderRadius_2} ${theme.borderRadius_2}`}
          >
            <Divider margin={0} />
            <FilterSection
              key={`filter-section-${formState.ruleType}`}
              availableDimensions={props.availableDimensions}
              dimensionPreferences={props.dimensionPreferences}
              dimensionValues={props.dimensionValues}
              form={form}
              isLoadingDimensionPreferences={
                props.isLoadingDimensionPreferences
              }
              isLoadingDimensionValues={props.isLoadingDimensionValues}
              required
              onInteraction={props.onInteraction}
            />
          </Box>
        );
      }
      case BillingRuleType.REALLOCATE_COSTS: {
        return (
          <BillingRuleFormReallocateCosts
            availableDimensions={props.availableDimensions}
            dimensionPreferences={props.dimensionPreferences}
            dimensionValues={props.dimensionValues}
            form={form}
            isLoadingDimensionPreferences={props.isLoadingDimensionPreferences}
            isLoadingDimensionValues={props.isLoadingDimensionValues}
            isLoadingSubAccountIDs={props.isLoadingSubAccountIDs}
            subAccountIDs={props.subAccountIDs}
            onInteraction={props.onInteraction}
          />
        );
      }
      case BillingRuleType.RERATE: {
        return (
          <BillingRuleFormRerate
            availableDimensions={props.availableDimensions}
            dimensionPreferences={props.dimensionPreferences}
            dimensionValues={props.dimensionValues}
            form={form}
            isLoadingDimensionPreferences={props.isLoadingDimensionPreferences}
            isLoadingDimensionValues={props.isLoadingDimensionValues}
            onInteraction={props.onInteraction}
          />
        );
      }
      default:
        return null;
    }
  }

  function renderTenantsSelector() {
    // Only show tenant selector if on MSP Admin page.
    if (!props.isMspAdmin) {
      return null;
    }

    return (
      <>
        <Flex direction="column" gap={theme.space_xs}>
          <Flex alignItems="center" justifyContent="space-between">
            <Text fontSize="18px">{copyText.tenantsSectionTitle}</Text>
            <form.Field
              name="applyToAllTenants"
              children={(field) => {
                return (
                  <LabeledSwitch
                    checked={field.state.value}
                    label={copyText.applyToAllTenantsInputLabel}
                    size="small"
                    onChange={(checked) => {
                      field.handleChange(checked);

                      if (checked) {
                        form.setFieldValue("applyToTenants", []);

                        form.setFieldMeta("applyToTenants", (meta) => ({
                          ...meta,
                          errors: [],
                          errorMap: {},
                        }));
                      }
                    }}
                  />
                );
              }}
            />
          </Flex>
          <Box
            backgroundColor={theme.panel_backgroundColor}
            borderRadius={theme.borderRadius_2}
            padding={theme.space_md}
          >
            <form.Field
              name="applyToTenants"
              mode="array"
              validators={{
                onChangeListenTo: ["applyToAllTenants"],
                onChange: ({ value, fieldApi }) => {
                  const applyToAllTenants =
                    fieldApi.form.getFieldValue("applyToAllTenants");

                  return applyToAllTenants || value.length > 0
                    ? undefined
                    : copyText.validationErrorMessageSelectAtLeastOneTenant;
                },
                onMount: ({ value, fieldApi }) => {
                  const applyToAllTenants =
                    fieldApi.form.getFieldValue("applyToAllTenants");

                  return applyToAllTenants || value.length > 0
                    ? undefined
                    : copyText.validationErrorMessageSelectAtLeastOneTenant;
                },
              }}
              children={(field) => {
                const filteredTenants = props.tenants.filter(
                  (tenant) =>
                    !field.state.value.some((val) => val.id === tenant.id)
                );

                const tenantOptions = orderBy(filteredTenants, "name").map(
                  (tenant) => ({
                    label: tenant.name,
                    value: tenant.id,
                    onClick: () =>
                      field.pushValue({
                        id: tenant.id,
                        name: tenant.name,
                      }),
                  })
                );

                return (
                  <>
                    <Box>
                      {field.state.value.map((_, i) => {
                        return (
                          <form.Field
                            key={i}
                            name={`applyToTenants[${i}].name`}
                            children={(subField) => {
                              return (
                                <Flex
                                  alignItems="center"
                                  gap={theme.space_xs}
                                  marginBottom={theme.space_xs}
                                >
                                  <Flex
                                    border={`1px solid ${theme.border_color}`}
                                    borderRadius={theme.borderRadius_1}
                                    flexGrow={1}
                                    padding={theme.space_sm}
                                  >
                                    <Text lineHeight="20px">
                                      {subField.state.value}
                                    </Text>
                                  </Flex>
                                  <Button
                                    iconStart={
                                      <Icon color={"#535862"} icon={faTrash} />
                                    }
                                    type="button"
                                    onClick={() => field.removeValue(i)}
                                  />
                                </Flex>
                              );
                            }}
                          />
                        );
                      })}
                    </Box>
                    <Box width={200}>
                      <Dropdown options={tenantOptions} placement="bottom-end">
                        <Button
                          iconStart={<Icon icon={faPlus} />}
                          size="tiny"
                          type="button"
                        >
                          {copyText.addTenantButtonLabel}
                        </Button>
                      </Dropdown>
                    </Box>
                  </>
                );
              }}
            />
          </Box>
        </Flex>
        {!props.billingRule ? (
          <Box marginBottom={theme.space_sm}>
            <Divider color={"#C2C8D0"} direction="vertical" minHeight={24} />
          </Box>
        ) : null}
      </>
    );
  }

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Flex direction="column" gap={theme.space_xs}>
        <Text fontSize="18px">{copyText.detailsSectionTitle}</Text>
        <Box
          backgroundColor={theme.panel_backgroundColor}
          borderRadius={theme.borderRadius_2}
          padding={theme.space_md}
        >
          <form.Field
            name="name"
            validators={{
              onChange: z.string().nonempty(copyText.errorInputFieldRequired),
              onMount: z.string().nonempty(),
            }}
            children={(field) => {
              const shouldShowError =
                field.state.meta.isTouched &&
                field.state.meta.errors.length > 0;

              const errorCaption = shouldShowError
                ? field.state.meta.errors.join(", ")
                : undefined;

              const variant = shouldShowError ? "danger" : undefined;

              return (
                <FormField
                  input={TextInput}
                  errorCaption={errorCaption}
                  label={copyText.nameInputLabel}
                  placeholder={copyText.nameInputPlaceholder}
                  required
                  value={field.state.value}
                  variant={variant}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              );
            }}
          />
          <Flex gap={theme.space_md}>
            <form.Subscribe
              selector={(state) => state.values.endDate}
              children={(endDate) => {
                return (
                  <form.Field
                    name="startDate"
                    validators={{
                      onChange: z
                        .nullable(z.date())
                        .refine((value) => value !== null),
                      onMount: z
                        .nullable(z.date())
                        .refine((value) => value !== null),
                    }}
                    children={(field) => {
                      return (
                        <FormField
                          label={copyText.startDateInputLabel}
                          marginBottom={0}
                          required
                        >
                          <DatePicker
                            customInput={
                              <CustomSelectInput
                                value={
                                  field.state.value
                                    ? formatDate(
                                        field.state.value,
                                        "MM/dd/yyyy"
                                      )
                                    : undefined
                                }
                                onClear={() => field.handleChange(null)}
                              />
                            }
                            endDate={endDate}
                            selected={field.state.value}
                            selectsStart
                            startDate={field.state.value}
                            onChange={(date) => field.handleChange(date)}
                          />
                        </FormField>
                      );
                    }}
                  />
                );
              }}
            />
            <form.Subscribe
              selector={(state) => state.values.startDate}
              children={(startDate) => {
                return (
                  <form.Field
                    name="endDate"
                    children={(field) => {
                      return (
                        <FormField
                          label={copyText.endDateInputLabel}
                          marginBottom={0}
                          optional
                        >
                          <DatePicker
                            customInput={
                              <CustomSelectInput
                                value={
                                  field.state.value
                                    ? formatDate(
                                        field.state.value,
                                        "MM/dd/yyyy"
                                      )
                                    : undefined
                                }
                                onClear={() => field.handleChange(null)}
                              />
                            }
                            endDate={field.state.value}
                            selected={field.state.value}
                            selectsEnd
                            startDate={startDate}
                            onChange={(date) => field.handleChange(date)}
                          />
                        </FormField>
                      );
                    }}
                  />
                );
              }}
            />
          </Flex>
        </Box>
      </Flex>
      <Box marginBottom={theme.space_sm}>
        <Divider color={"#C2C8D0"} direction="vertical" minHeight={24} />
      </Box>
      <Flex direction="column" gap={theme.space_xs}>
        <Text fontSize="18px">{copyText.ruleSectionTitle}</Text>
        <Box
          backgroundColor={theme.panel_backgroundColor}
          borderRadius={
            !formState.ruleType ||
            formState.ruleType === BillingRuleType.REALLOCATE_COSTS
              ? theme.borderRadius_2
              : `${theme.borderRadius_2} ${theme.borderRadius_2} 0 0`
          }
          padding={theme.space_md}
        >
          <form.Field
            name="ruleType"
            validators={{
              onChange: z
                .nativeEnum(BillingRuleType)
                .refine((value) => value !== null),
              onMount: z
                .nativeEnum(BillingRuleType)
                .refine((value) => value !== null),
            }}
            children={(field) => {
              const selectedValue = ruleTypeOptions.find(
                (option) => option.value === field.state.value
              );

              return (
                <FormField
                  label={copyText.typeInputLabel}
                  marginBottom={0}
                  required
                >
                  <Select
                    options={ruleTypeOptions}
                    value={selectedValue}
                    onChange={(option) => {
                      if (option) {
                        field.handleChange(option.value);
                      }

                      // We need to fully reset the filters and it's validation state if the rule type is changed.
                      form.setFieldValue("filters", [defaultFilter]);

                      form.setFieldMeta("filters[0].schemaName", (meta) => ({
                        ...meta,
                        errors: [],
                        errorMap: {},
                      }));

                      form.setFieldMeta("filters[0].values", (meta) => ({
                        ...meta,
                        errors: [],
                        errorMap: {},
                      }));
                    }}
                  />
                </FormField>
              );
            }}
          />
        </Box>
      </Flex>
      {renderFormContent()}
      <Box marginBottom={theme.space_sm}>
        {!props.billingRule && !props.isMspAdmin ? (
          <Divider color={"#C2C8D0"} direction="vertical" minHeight={24} />
        ) : null}
      </Box>
      {renderTenantsSelector()}
      {!props.billingRule ? (
        <BillingRuleFormPriority
          form={form}
          isLoadingBillingRules={props.isLoadingBillingRules}
        />
      ) : null}
      <form.Subscribe
        children={(state) => {
          let disabled =
            !state.canSubmit || !state.isTouched || props.isProcessing;

          if (props.billingRule) {
            const hasChanged = !isEqual(state.values, _defaultValues);

            disabled = disabled || !hasChanged;
          }

          return (
            <Flex
              gap={theme.space_xs}
              justifyContent="flex-end"
              marginTop={theme.space_lg}
            >
              <Button
                type="button"
                width={100}
                onClick={() => {
                  props.onInteraction({
                    type: BillingRuleForm.INTERACTION_CANCEL_BUTTON_CLICKED,
                  });
                }}
              >
                {copyText.cancelButtonLabel}
              </Button>
              <Button disabled={disabled} primary type="submit" width={100}>
                {props.isProcessing ? (
                  <LoadingSpinner />
                ) : (
                  copyText.submitButtonLabel
                )}
              </Button>
            </Flex>
          );
        }}
      />
    </Form>
  );
}

BillingRuleForm.INTERACTION_CANCEL_BUTTON_CLICKED =
  "BillingRuleForm.INTERACTION_CANCEL_BUTTON_CLICKED" as const;

BillingRuleForm.INTERACTION_SUBMIT_BUTTON_CLICKED =
  "BillingRuleForm.INTERACTION_SUBMIT_BUTTON_CLICKED" as const;

BillingRuleForm.INTERACTION_UPDATE_DIMENSIONS =
  "BillingRuleForm.INTERACTION_UPDATE_DIMENSIONS" as const;

type InteractionCancelButtonClicked = {
  type: typeof BillingRuleForm.INTERACTION_CANCEL_BUTTON_CLICKED;
};

type InteractionSubmitButtonClicked = {
  type: typeof BillingRuleForm.INTERACTION_SUBMIT_BUTTON_CLICKED;
  values: SubmitValues;
};

type InteractionUpdateDimensions = {
  type: typeof BillingRuleForm.INTERACTION_UPDATE_DIMENSIONS;
  dimensionName: string;
};

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace BillingRuleForm {
  export type Interaction =
    | InteractionCancelButtonClicked
    | InteractionSubmitButtonClicked
    | InteractionUpdateDimensions;
}
